import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails
} from 'amazon-cognito-identity-js'

import * as AWS from 'aws-sdk'

export const DEFAULT_PART_SIZE = 10 * 1024 * 1024
export const DEFAULT_QUEUE_SIZE = 4

const {REACT_APP_STAGE: stage, REACT_APP_AWS_DEFAULT_REGION: region} = process.env

const {
  [`REACT_APP_USER_POOL_ID_${stage}`]: userPoolId,
  [`REACT_APP_APP_CLIENT_ID_${stage}`]: clientId,
  [`REACT_APP_USER_IDENTITY_POOL_ID_${stage}`]: identityPoolId
} = process.env

AWS.config.region = region

// Initialize Lambda only after AWS Config credentials are set in the authentication processes
let lambda

// Reference Doc: https://github.com/aws-amplify/amplify-js/blob/main/packages/amazon-cognito-identity-js/README.md
const userPool = new CognitoUserPool({
  UserPoolId: userPoolId,
  ClientId: clientId
})

export const authUser = () => {
  return new Promise(async(resolve, reject) => {
    const currentUser = userPool.getCurrentUser()

    if (currentUser != null) {
      currentUser.getSession((err, session) => {
        if (err) {
          return reject(err)
        }

        const sessionIsValid = session.isValid()

        if (sessionIsValid) {
          const credentialParams = {
            IdentityPoolId: identityPoolId,
            Logins: {
              [`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: session.getIdToken().getJwtToken()
            }
          }

          let credentials = new AWS.CognitoIdentityCredentials(credentialParams)
          credentials.clearCachedId()

          credentials = new AWS.CognitoIdentityCredentials(credentialParams)

          AWS.config.region = region
          AWS.config.credentials = credentials
          AWS.config.credentials.refresh(error => {
            if (error) {
              reject(error)
            } else {
              resolve({
                authenticated: true,
                user: currentUser
              })
            }
          })

          lambda = new AWS.Lambda()
        }
        else {
          reject()
        }
      })
    }
    else {
      reject()
    }
  })
}

export const login = (username, password) => {
  const currentUser = new CognitoUser({
    Username: username,
    Pool: userPool
  })

  const authenticationDetails = new AuthenticationDetails({
    Username: username,
    Password: password
  })

  return new Promise((resolve, reject) =>
    currentUser.authenticateUser(authenticationDetails, {
      onSuccess: result => {
        const idToken = result.getIdToken().getJwtToken()

        const credentialParams = {
          IdentityPoolId: identityPoolId,
          Logins: {
            [`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: idToken
          }
        }

        let credentials = new AWS.CognitoIdentityCredentials(credentialParams)
        credentials.clearCachedId()

        credentials = new AWS.CognitoIdentityCredentials(credentialParams)

        AWS.config.region = region
        AWS.config.credentials = credentials
        AWS.config.credentials.refresh(error => {
          if (error) {
            reject(error)
          } else {
            resolve({
              authenticated: true,
              user: currentUser
            })
          }
        })

        lambda = new AWS.Lambda()
      },
      onFailure: err => {
        if (err.code === 'PasswordResetRequiredException') {
          resolve({
            authenticated: false,
            passwordReset: true,
            user: currentUser
          })
        }

        reject(err)
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        resolve({
          authenticated: false,
          newPasswordRequired: true,
          user: currentUser
        })
      }
    })
  )
}

export const passwordReset = (user, newpassword) => {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newpassword, {email: user.username}, {
      onSuccess: result => resolve(),
      onFailure: err => {
        reject(err)
      }
    })
  })
}

export const passwordResetWithCode = (email, newpassword, code) => {
  const user = new CognitoUser({Username: email, Pool: userPool})

  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newpassword, {
      onSuccess: result => resolve(),
      onFailure: err => {
        reject(err)
      }
    })
  })
}

export const sendCode = email => {
  const user = new CognitoUser({Username: email, Pool: userPool})

  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: result => resolve(),
      onFailure: err => {
        reject(err)
      }
    })
  })
}

export const getUserToken = user => {
  return new Promise((resolve, reject) => {
    user.getSession((err, session) => {
      if (err) {
        return reject(err)
      }

      resolve(session)
    })
  })
}

export const getUserAttributes = user => {
  return new Promise((resolve, reject) => {
    user.getUserAttributes((err, res) => {
      if (err) {
        reject(err)
      }

      let attr = {}

      if (res) {
        res.map((a) => {
          return attr[a.getName()] = a.getValue()
        })

        resolve(attr)
      }
      else {
        resolve()
      }
    })
  })
}

export const signOutUser = () => {
  const currentUser = userPool.getCurrentUser()

  if (currentUser !== null) {
    currentUser.signOut()
  }
}

export const getSession = user => {
  return new Promise((resolve, reject) => {
    user.getSession((err, res) => {
      if (err) {
        return reject(err)
      }

      resolve(res)
    })
  })
}

export const callLambda = (functionName, functionAlias, body) => {
  return new Promise((resolve, reject) => {
    lambda.invoke({
      FunctionName: functionName,
      Qualifier: functionAlias,
      Payload: JSON.stringify(body)
    }, (err, data) => {
      if (err) {
        console.error('[ERROR]: ' + err)
        return reject(new Error(err))
      }
      else if (data['FunctionError']) {
        const payload = JSON.parse(data.Payload)
        console.error('[PAYLOAD]: ' + JSON.stringify(payload))

        return reject(new Error(payload.errorMessage))
      }
      else {
        let body = JSON.parse(data.Payload).body
        const dataToReturn = JSON.parse(body)

        return resolve(dataToReturn)
      }
    })
  })
}

export const callApexRest = (path, bodyParams, concatenateToEnd) => {
  return new Promise(async(resolve, reject) => {
    try {
      const {
        [`REACT_APP_FUNCTION_NAME_${stage}`]: functionName,
        [`REACT_APP_FUNCTION_ALIAS_${stage}`]: functionAlias,
        [`REACT_APP_APEX_REST_POST_${path}`]: resourcePath
      } = process.env

      let fullResourcePath = resourcePath

      if(concatenateToEnd) {
        fullResourcePath += '/' + concatenateToEnd
      }

      const currentUser = userPool.getCurrentUser()

      // Previously, a null user or invalid session left the Promise neither
      // resolved nor rejected — it hung forever, so callers' catch blocks never
      // ran (infinite spinner, no error). Always settle: reject SESSION_EXPIRED.
      if (currentUser == null) {
        const e = new Error('Your session has expired. Please sign in again.')
        e.code = 'SESSION_EXPIRED'
        return reject(e)
      }

      const session = await getSession(currentUser)

      if (!session.isValid()) {
        const e = new Error('Your session has expired. Please sign in again.')
        e.code = 'SESSION_EXPIRED'
        return reject(e)
      }

      const accessToken = session.getAccessToken().getJwtToken()
      const data = await callLambda(functionName, functionAlias, {
        body: {
          accessToken,
          resourcePath: fullResourcePath,
          ...bodyParams
        }
      })

      resolve(data)
    }
    catch (e) {
      console.error('[ERROR]: ' + e)
      return reject(new Error('There was an error fetching. Please refresh and try again.  '))
    }
  })
}

export const registerUpload = upload => {
  let metadata = {}

  upload.metadata.forEach(m => {
    metadata[m.name] = m.value
  })

  const params = {
    Bucket: upload.bucket,
    Key: upload.key,
    Body: upload.payload,
    Metadata: metadata
  }

  const tags = upload.tags.map(tag => ({'Key': tag.name, 'Value': tag.value}))

  return new AWS.S3.ManagedUpload({
    partSize: DEFAULT_PART_SIZE,
    queueSize: DEFAULT_QUEUE_SIZE,
    params,
    tags,
    computeChecksums: true
  })
}